import React from "react";

import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Autocomplete, Box, Button, IconButton, Stack, TextField } from "@mui/material";
import { ThinPlusInCircle } from "assets/material-custom-icons";
import { NumberInput } from "components/forms";
import { capitalize } from "lodash";

import { managementFeeBasis, State } from "./FeesForm";

interface Props {
  items: State["differential"];
  setState: React.Dispatch<React.SetStateAction<State>>;
}

export const DifferentialMethod: React.FC<Props> = ({ items, setState }) => {
  const handleAddItem = () => {
    const maxId = items.reduce((acc, item) => (item.id > acc ? item.id : acc), 0);

    const defaultItem: State["differential"][0] = {
      id: maxId + 1,
      year: maxId + 1,
      rate: null,
      type: managementFeeBasis[0]
    };

    setState((prevState) => ({ ...prevState, differential: [...prevState.differential, defaultItem] }));
  };

  const handleValueChange = ({
    id,
    key,
    value
  }: {
    id: number;
    key: keyof State["differential"][0];
    value: string | number | null;
  }) => {
    setState((prevState) => ({
      ...prevState,
      differential: prevState.differential.map((item) => {
        if (item.id !== id) return item;

        return { ...item, [key]: value };
      })
    }));
  };

  const handleDeleteItem = (id: number) => {
    setState((prevState) => ({
      ...prevState,
      differential: prevState.differential.filter((item) => item.id !== id)
    }));
  };

  return (
    <Stack sx={{ gap: "20px" }}>
      {items.map(({ id, year, rate, type }, index) => (
        <Stack key={id} sx={{ flexDirection: "row", gap: "10px" }}>
          <NumberInput
            placeholder="1"
            fullWidth
            disabled
            label="Year"
            value={year}
            numberInputProps={{
              allowNegative: false,
              decimalScale: 0,
              onValueChange: ({ floatValue }) => handleValueChange({ id, key: "year", value: floatValue || null })
            }}
          />
          <NumberInput
            placeholder="0.5%"
            fullWidth
            label="Rate"
            value={rate}
            numberInputProps={{
              allowNegative: false,
              decimalScale: 2,
              suffix: "%",
              onValueChange: ({ floatValue }) => handleValueChange({ id, key: "rate", value: floatValue || null })
            }}
          />
          <Autocomplete
            fullWidth
            disableClearable
            value={type}
            options={managementFeeBasis}
            renderOption={(props, item) => (
              <li {...props} key={item}>
                {capitalize(item)}
              </li>
            )}
            onChange={(_, option) => handleValueChange({ id, key: "type", value: option })}
            renderInput={(params) => <TextField {...params} label="Management Fee Basis" />}
          />
          {items.length > 1 && (
            <Box sx={{ width: 32, height: 32, flexShrink: 0 }}>
              {index === items.length - 1 && (
                <IconButton onClick={() => handleDeleteItem(id)}>
                  <CancelOutlinedIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Stack>
      ))}
      <Button size="sm" startIcon={<ThinPlusInCircle />} sx={{ alignSelf: "flex-start" }} onClick={handleAddItem}>
        Add Year
      </Button>
    </Stack>
  );
};
